import { inject, Injectable } from "@angular/core";
import { EstimateWeb3MigrationGQLService } from "app/api/graphql/offer/generated/web3.migration.estimation.service.graphql-gen";
import { SignUpForForWeb3MigrationGQLService } from "app/api/graphql/offer/generated/web3.migration.signup.service.graphql-gen";
import { Web3MigrationGQLService } from "app/api/graphql/offer/generated/web3.migration.status.service.graphql-gen";
import { catchError, map, Observable } from "rxjs";
import {
	IWeb3MigrationEstimationResponse,
	IWeb3MigrationMigrateResponse,
	IWeb3MigrationSignupResponse,
	IWeb3MigrationStatusResponse
} from "../interface/web3.remote.interface";
import { ExceptionsService } from "@core-services/exceptions.service";
import { MigrateToWeb3GQLService } from "app/api/graphql/offer/generated/web3.migration.migrate.service.graphql-gen";
import {
	UserSettingsFetchGQLService,
	UserSettingsFetchQuery
} from "app/api/graphql/offer/generated/user.settings.service.graphql-gen";

@Injectable({
	providedIn: "root"
})
export class Web3RemoteService {
	private web3StatusGQL = inject(Web3MigrationGQLService);
	private web3EstimateGQL = inject(EstimateWeb3MigrationGQLService);
	private web3SignupGQL = inject(SignUpForForWeb3MigrationGQLService);
	private web3MigrateGQL = inject(MigrateToWeb3GQLService);
	private userSettingsGQL = inject(UserSettingsFetchGQLService);
	private exceptionsService = inject(ExceptionsService);

	/**
	 * Fetch web3 migration status
	 * @returns - IWeb3MigrationStatusResponse as Observable
	 */
	getWeb3Status(): Observable<IWeb3MigrationStatusResponse> {
		return this.web3StatusGQL.watch(null, { nextFetchPolicy: "network-only" }).valueChanges.pipe(
			map(
				({
					data: {
						web3Migration: { signedUp }
					}
				}) => ({ signedUp })
			),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow)
		);
	}

	/**
	 * Fetch web3 migration estimation
	 * @returns - IWeb3MigrationEstimationResponse as Observable
	 */
	getWeb3Estimation(): Observable<IWeb3MigrationEstimationResponse> {
		return this.web3EstimateGQL.watch(null, { nextFetchPolicy: "network-only" }).valueChanges.pipe(
			map(
				({
					data: {
						estimateWeb3Migration: {
							amount: { value },
							userJourney
						}
					}
				}) => ({ userJourney, amount: value })
			),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow)
		);
	}

	/**
	 * Sign up for web3 migration
	 * @returns - IWeb3MigrationSignupResponse as Observable
	 */
	signUpForWeb3Migration(): Observable<IWeb3MigrationSignupResponse> {
		return this.web3SignupGQL.mutate(null).pipe(
			map(
				({
					data: {
						signUpForWeb3Migration: {
							web3Migration: { signedUp },
							code
						}
					}
				}) => ({ signedUp, code })
			),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow)
		);
	}

	/**
	 * Migrate to web3
	 * @returns - IWeb3MigrationSignupResponse as Observable
	 */
	migrateToWeb3(): Observable<IWeb3MigrationMigrateResponse> {
		return this.web3MigrateGQL.mutate(null).pipe(
			map(
				({
					data: {
						migrateToWeb3: {
							code,
							amount: { value }
						}
					}
				}) => ({ code, value })
			),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow)
		);
	}

	/**
	 * Fetch user settings
	 * @returns - UserSettingsFetchQuery["userSettings"] as Observable
	 */
	getUserSettings(): Observable<UserSettingsFetchQuery["userSettings"]> {
		return this.userSettingsGQL.watch(null, { nextFetchPolicy: "network-only" }).valueChanges.pipe(
			map(({ data: { userSettings } }) => userSettings),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow)
		);
	}
}
